import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import ReferralLeaderboard from "@/components/referral/ReferralLeaderboard";
import { Users, TrendingUp, DollarSign, Target } from "lucide-react";

export default function ReferralAnalytics() {
  const [stats, setStats] = useState({
    totalPartners: 0,
    activePartners: 0,
    totalReferrals: 0,
    totalPipeline: 0,
    totalCommissionOwed: 0,
    totalCommissionPaid: 0,
    conversionRate: 0,
  });
  const [referralsByStatus, setReferralsByStatus] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      // Load partners stats
      const { data: partnersData } = await supabase.from("referral_partners").select("*");
      
      const totalPartners = partnersData?.length || 0;
      const activePartners = partnersData?.filter(p => p.status === "active").length || 0;
      const totalCommissionOwed = partnersData?.reduce((sum, p) => sum + (p.total_commission_earned || 0), 0) || 0;
      const totalCommissionPaid = partnersData?.reduce((sum, p) => sum + (p.total_commission_paid || 0), 0) || 0;

      // Load referrals stats
      const { data: referralsData } = await supabase.from("referrals").select("*");
      
      const totalReferrals = referralsData?.length || 0;
      const totalPipeline = referralsData?.reduce((sum, r) => sum + (r.contract_value || 0), 0) || 0;
      const closedWon = referralsData?.filter(r => r.status === "closed_won").length || 0;
      const conversionRate = totalReferrals > 0 ? (closedWon / totalReferrals) * 100 : 0;

      // Group referrals by status
      const statusGroups = referralsData?.reduce((acc: any, ref) => {
        const status = ref.status;
        if (!acc[status]) {
          acc[status] = { name: status.replace(/_/g, " ").toUpperCase(), value: 0 };
        }
        acc[status].value += 1;
        return acc;
      }, {});

      setStats({
        totalPartners,
        activePartners,
        totalReferrals,
        totalPipeline,
        totalCommissionOwed,
        totalCommissionPaid,
        conversionRate,
      });

      setReferralsByStatus(Object.values(statusGroups || {}));
    } catch (error) {
      console.error("Error loading analytics:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const COLORS = ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#6b7280", "#ec4899", "#14b8a6"];

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Referral Analytics</h1>
          <p className="text-muted-foreground">Track partner performance and program effectiveness</p>
        </div>

        {/* Overview Stats */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Partners</p>
                <p className="text-2xl font-bold">{stats.totalPartners}</p>
                <p className="text-xs text-muted-foreground">{stats.activePartners} active</p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Referrals</p>
                <p className="text-2xl font-bold">{stats.totalReferrals}</p>
                <p className="text-xs text-muted-foreground">{stats.conversionRate.toFixed(1)}% closed</p>
              </div>
              <Target className="h-8 w-8 text-muted-foreground" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pipeline Value</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.totalPipeline)}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-muted-foreground" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Commission</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.totalCommissionPaid)}</p>
                <p className="text-xs text-muted-foreground">
                  {formatCurrency(stats.totalCommissionOwed - stats.totalCommissionPaid)} pending
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-muted-foreground" />
            </div>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Referrals by Status */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Referral Pipeline</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={referralsByStatus}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {referralsByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          {/* Leaderboard */}
          <ReferralLeaderboard />
        </div>
      </div>
    </DashboardLayout>
  );
}
